import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { apiDelete, apiGet } from "../../shared/apiClient";
import { getApiBaseUrl } from "../../config/appSettings";
import { useToast } from "../../shared/ToastContext";
import { blogInputClass } from "./BlogEditorFormFields";

function blogInitials(title) {
  if (!title || !String(title).trim()) return "?";
  const t = String(title).trim();
  return t.length >= 2 ? t.slice(0, 2).toUpperCase() : `${t[0].toUpperCase()}?`;
}

function PlusIcon({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function RefreshCwIcon({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M3 21v-5h5" />
    </svg>
  );
}

function PencilIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  );
}

function EyeIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function TrashIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );
}

export function BlogsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const [customers, setCustomers] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [search, setSearch] = useState("");
  const [filterCustomerId, setFilterCustomerId] = useState("");
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [listRefreshing, setListRefreshing] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams();
    p.set("page", String(page));
    p.set("pageSize", String(pageSize));
    if (search.trim()) p.set("search", search.trim());
    if (isAdmin && filterCustomerId) p.set("customerId", filterCustomerId);
    const res = await apiGet(`/api/Blogs?${p.toString()}`);
    if (res.ok) {
      setRows(res.data?.items ?? []);
      setTotal(res.data?.totalCount ?? 0);
    } else toast.push(String(res.error), "error");
    setLoading(false);
  }, [page, search, filterCustomerId, isAdmin, toast]);

  useEffect(() => {
    void (async () => {
      if (isAdmin) {
        const c = await apiGet("/api/Customers");
        if (c.ok) setCustomers(c.data ?? []);
      }
    })();
  }, [isAdmin]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    if (!deleteTarget) return;
    const onKey = (e) => {
      if (e.key === "Escape") setDeleteTarget(null);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [deleteTarget]);

  const handleListRefresh = async () => {
    setListRefreshing(true);
    await loadList();
    setListRefreshing(false);
  };

  const runDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const res = await apiDelete(`/api/Blogs/${deleteTarget.id}`);
    setDeleting(false);
    if (!res.ok) {
      toast.push(String(res.error), "error");
      return;
    }
    toast.push("Deleted", "success");
    void loadList();
    setDeleteTarget(null);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="w-full space-y-4">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Blogs</h1>
        <p className="mt-1 text-sm text-slate-500">Create rich posts with images, links, and formatting</p>
        <div className="mt-5 flex flex-col gap-3 sm:mt-6 sm:flex-row sm:items-center">
          <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-stretch sm:gap-3">
            <div className="relative min-w-0 flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden>
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="7" />
                  <line x1="16.5" y1="16.5" x2="21" y2="21" />
                </svg>
              </span>
              <input
                type="search"
                className={`${blogInputClass} pl-9`}
                placeholder="Search title, subtitle, author…"
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
                aria-label="Search blogs"
              />
            </div>
            {isAdmin && (
              <select
                className={blogInputClass + " w-full min-w-0 sm:w-56 sm:shrink-0"}
                value={filterCustomerId}
                onChange={(e) => {
                  setPage(1);
                  setFilterCustomerId(e.target.value);
                }}
                aria-label="Filter by customer"
              >
                <option value="">All customers</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} {c.code ? `(${c.code})` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="flex shrink-0 items-center justify-end gap-3">
            <div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600">
              {total} {total === 1 ? "post" : "posts"}
            </div>
            <button
              type="button"
              onClick={() => void handleListRefresh()}
              disabled={listRefreshing || loading}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-sky-600 shadow-sm transition hover:bg-sky-50 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
              title="Refresh list"
              aria-label="Refresh blog list"
            >
              {listRefreshing ? (
                <span
                  className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-sky-200 border-t-sky-600"
                  aria-hidden
                />
              ) : (
                <RefreshCwIcon className="h-5 w-5" />
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate("/blogs/new")}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-800 text-white shadow-md transition hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-600/40"
              title="Add blog"
              aria-label="Add blog"
            >
              <PlusIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3 sm:px-5">
          <h2 className="text-sm font-semibold text-slate-800">All blogs</h2>
        </div>
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
            <span
              className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600"
              aria-hidden
            />
            Loading…
          </div>
        ) : (
          <div className="p-4 sm:p-5">
            {rows.length === 0 ? (
              <p className="py-12 text-center text-sm text-slate-500">
                No blogs found. Use the + button to add one or adjust your search.
              </p>
            ) : (
              <ul className="grid list-none grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {rows.map((r) => {
                  const coverSrc =
                    r.image && String(r.image).trim()
                      ? String(r.image).startsWith("http")
                        ? r.image
                        : `${getApiBaseUrl()}${r.image}`
                      : null;
                  const customerLabel =
                    r.customerName || r.customerCode || (r.customerId != null ? String(r.customerId) : null);
                  return (
                    <li key={r.id}>
                      <article
                        className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm ring-1 ring-slate-900/[0.04] transition hover:border-slate-300/80 hover:shadow-md hover:ring-slate-900/[0.06]"
                      >
                        <div className="relative h-28 shrink-0 overflow-hidden bg-slate-100 sm:h-32">
                          {coverSrc ? (
                            <img
                              src={coverSrc}
                              alt=""
                              className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-brand-500 via-brand-700 to-brand-900">
                              <span
                                className="rounded-md bg-white/20 px-2.5 py-1 text-sm font-bold tracking-wide text-white shadow-inner"
                                aria-hidden
                              >
                                {blogInitials(r.title)}
                              </span>
                            </div>
                          )}
                          <div className="absolute right-2 top-2 z-10 flex flex-col gap-1">
                            <button
                              type="button"
                              onClick={() => navigate(`/blogs/${r.id}/edit`)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-emerald-200 bg-white/95 text-emerald-700 shadow-sm backdrop-blur transition hover:bg-emerald-50 hover:text-emerald-800"
                              title="Edit"
                              aria-label={`Edit ${r.title || "blog"}`}
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => navigate(`/blogs/${r.id}`)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-indigo-200 bg-white/95 text-indigo-700 shadow-sm backdrop-blur transition hover:bg-indigo-50 hover:text-indigo-800"
                              title="View"
                              aria-label={`View ${r.title || "blog"}`}
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteTarget({ id: r.id, title: r.title || "Blog" })}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-200 bg-white/95 text-red-700 shadow-sm backdrop-blur transition hover:bg-red-50 hover:text-red-800"
                              title="Delete"
                              aria-label={`Delete ${r.title || "blog"}`}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        <div className="flex min-h-0 flex-1 flex-col p-3">
                          <h3 className="line-clamp-2 text-sm font-semibold leading-snug tracking-tight text-slate-900 sm:text-[0.95rem]">
                            {r.title || "—"}
                          </h3>
                          {r.subtitle && (
                            <p className="mt-0.5 line-clamp-1 text-xs text-slate-500">{r.subtitle}</p>
                          )}
                          {r.shortDescription && (
                            <p className="mt-1 line-clamp-2 text-xs text-slate-600">{r.shortDescription}</p>
                          )}
                          <div className="mt-2 flex flex-1 flex-col justify-end space-y-0.5 text-[11px] text-slate-500">
                            {r.date && (
                              <p className="flex items-center gap-1.5 text-slate-500">
                                <span className="shrink-0 text-slate-400" aria-hidden>
                                  <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="4" width="18" height="18" rx="2" />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                  </svg>
                                </span>
                                <span className="min-w-0 truncate">{new Date(r.date).toLocaleString()}</span>
                              </p>
                            )}
                            {r.authorName && (
                              <p className="line-clamp-1">
                                <span className="font-medium text-slate-400">By</span> {r.authorName}
                              </p>
                            )}
                            {isAdmin && customerLabel && (
                              <p className="line-clamp-1 text-slate-500" title={customerLabel}>
                                <span className="text-slate-400">· </span>
                                {customerLabel}
                              </p>
                            )}
                          </div>
                        </div>
                      </article>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {!loading && total > 0 && (
          <div className="flex flex-col gap-2 border-t border-slate-100 bg-slate-50/50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <span className="text-xs text-slate-500 sm:text-sm">
              Page {page} of {totalPages}
            </span>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {deleteTarget && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="presentation">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/50"
            onClick={() => setDeleteTarget(null)}
            aria-label="Close"
          />
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-blog-title"
            className="relative z-10 w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-1 flex h-10 w-10 items-center justify-center rounded-full bg-red-100 text-red-600" aria-hidden>
              <TrashIcon className="h-5 w-5" />
            </div>
            <h2 id="delete-blog-title" className="mt-3 text-lg font-semibold text-slate-900">
              Delete blog?
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              This will remove <span className="font-medium text-slate-800">{deleteTarget.title}</span> permanently. This
              cannot be undone.
            </p>
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void runDelete()}
                disabled={deleting}
                className="rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-red-500 disabled:opacity-60"
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
